import {useCompanyTariffGuard} from '../../../modules/company-tariff';
import {FC, KeyboardEventHandler, useContext, useState} from 'react';
import {useForm} from 'react-hook-form';
import {useRecoilValue, useSetRecoilState} from 'recoil';

import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ClearIcon from '@mui/icons-material/Clear';
import Stack from '@mui/material/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';

import {
  deviceModelControlModeState,
  deviceModelControlState,
  deviceModelsPageable,
  deviceModelsQueryTrigger,
  deviceModelsState,
} from '../../../store/deviceModels/atoms';

import {EventBusContext} from '../../../providers/EventBus';

import {DictionaryService} from '../../../api/services/dictionary.service';
import {Button} from '@mui/material';
import {green} from '@mui/material/colors';

interface ITableToolbar {
  loadingState: 'hasValue' | 'hasError' | 'loading';
}

export const TableToolbar: FC<ITableToolbar> = ({loadingState}) => {
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const [loading, setLoading] = useState(false);
  const {control, getValues, watch, setValue} = useForm({defaultValues: {search: ''}});
  const pageable = useRecoilValue(deviceModelsPageable);
  const setDeviceModels = useSetRecoilState(deviceModelsState);
  const triggerDeviceModelsQuery = useSetRecoilState(deviceModelsQueryTrigger);
  const setOpenControl = useSetRecoilState(deviceModelControlState);
  const setModeOfControl = useSetRecoilState(deviceModelControlModeState);
  const {bus} = useContext(EventBusContext);

  const toAddDeviceModel = () => {
    if (!checkTariff()) return;
    setModeOfControl('ADD');
    setOpenControl(true);
  };

  const watchSearch = watch('search');

  const resetSearchField = () => {
    setValue('search', '', {shouldDirty: true});
    triggerDeviceModelsQuery((v) => v + 1);
  };

  const keyHandler: KeyboardEventHandler<HTMLDivElement> = (e) => {
    switch (e.code) {
      case 'Escape':
        resetSearchField();
        return;
      case 'Enter':
        watchSearch && onSubmit();
        return;
      default:
        return;
    }
  };

  const onSubmit = async () => {
    const data = getValues();

    try {
      setLoading(true);

      const res = await DictionaryService.getAllDeviceModels({
        spec: {
          modelName: data.search,
        },
        pageable,
      });
      setLoading(false);
      setDeviceModels(res.data);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  return (
    <>
      <Toolbar
        sx={{
          pl: {sm: 2},
          pr: {xs: 1, sm: 1},
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={2}>
          <ControlledInput
            onKeyUp={keyHandler}
            control={control}
            name="search"
            label="Поиск по названию модели"
            size="small"
            additionalFunc={(e) => {
              e?.currentTarget?.value?.length === 0 && onSubmit();
            }}
            fullWidth={true}
            sx={{width: 350}}
            InputProps={{
              endAdornment: (
                <>
                  {watchSearch ? (
                    <Tooltip title="Очистить">
                      <IconButton onClick={resetSearchField}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                  <LoadingButton
                    sx={{padding: '8px', minWidth: '40px'}}
                    onClick={onSubmit}
                    loading={loading}
                    disabled={!watchSearch}
                    size="small"
                  >
                    <SearchIcon />
                  </LoadingButton>
                </>
              ),
            }}
          />
          {loadingState === 'loading' && (
            <Stack direction="column" sx={{justifyContent: 'center'}}>
              <CircularProgress size="24px" />
            </Stack>
          )}
        </Stack>
        <Stack direction="row">
          <Tooltip title="Сформировать группу пользователей">
            <Button
              onClick={toAddDeviceModel}
              disabled={tariffLoading}
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              sx={{
                height: '40px',
                fontSize: '0.87rem',
                letterSpacing: '0',
                backgroundColor: green[500],
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: green[600],
                },
              }}
            >
              Добавить
            </Button>
          </Tooltip>
        </Stack>
      </Toolbar>
    </>
  );
};
